import { Component, signal } from '@angular/core';
import { SetupWizardLocationForm } from '../../../../../forms/setup-wizard/setup-wizard-location-form/setup-wizard-location-form';
import { SetupWizardLocationsTable } from './setup-wizard-locations-table/setup-wizard-locations-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardPayloadObjectKeys,
	SetupWizardLocation,
	SetupWizardLocationFormPayload,
	UserCsvKeys,
	SetupWizardUser,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import {
	makeAssignedColumn,
	makeHistoricalColumn,
	makeLocationsColumn,
	SetupWizardUsersTable,
} from '../setup-wizard-user-editor/setup-wizard-users-table/setup-wizard-users-table';

@Component({
	selector: 'app-setup-wizard-location-editor',
	imports: [
		SetupWizardLocationForm,
		SetupWizardLocationsTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		SetupWizardUsersTable,
		NzButtonModule,
	],
	standalone: true,
	templateUrl: './setup-wizard-location-editor.html',
	styleUrl: './setup-wizard-location-editor.scss',
})
export class SetupWizardLocationEditor {
	public edit = signal<SetupWizardLocation | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;
	public selectedTabIndex = signal<number>(0);
	public userTableColumns = signal<string[]>([
		UserCsvKeys.schoolIdOrEmail,
		UserCsvKeys.group,
		UserCsvKeys.firstName,
		UserCsvKeys.lastName,
		UserCsvKeys.active,
		makeLocationsColumn().key,
		makeAssignedColumn().key,
		makeHistoricalColumn().key,
	]);

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardLocationFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.locations, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEditUsers($event: SetupWizardLocation) {
		this.onEdit($event);
		this.selectedTabIndex.set(1);
	}

	public onEdit($event: SetupWizardLocation) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardLocation) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No location data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.locations, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardLocation) {
		if (!$event.after) {
			throw new Error('No location data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.locations, $event.after);
	}

	public async onRemove($event: SetupWizardLocation) {
		if (!$event.after) {
			throw new Error('No location data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.locations, $event.after);
	}

	public async onAssign($event: { user: SetupWizardUser; location: SetupWizardLocation }) {
		await this.setupWizard.assign($event.user, $event.location);
	}

	public async onUnassign($event: { user: SetupWizardUser; location: SetupWizardLocation }) {
		await this.setupWizard.unassign($event.user, $event.location);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
